import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { JivuCommandService } from './jivu-command.service';

type ProgressSnapshot = {
  progress: number;
  message?: string;
  jobId?: string;
};

type ProgressState = {
  lastSentAt: number;
  inFlight: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  pending: ProgressSnapshot | null;
};

@Injectable({ providedIn: 'root' })
export class JivuCommandProgressReporter {
  private readonly commandService = inject(JivuCommandService);
  private readonly states = new Map<string, ProgressState>();
  private readonly minIntervalMs = 2_500;
  private readonly timeoutMs = 4_000;

  report(commandId: string | null | undefined, progress: number | null | undefined, message?: string, jobId?: string): void {
    const id = this.cleanCommandId(commandId);
    if (!id || progress === null || progress === undefined) {
      return;
    }

    const state = this.stateFor(id);
    state.pending = {
      progress: this.normalizeProgress(progress),
      message,
      ...(jobId ? { jobId } : {})
    };
    this.schedule(id);
  }

  async reportNow(commandId: string | null | undefined, progress: number, message?: string, jobId?: string): Promise<void> {
    const id = this.cleanCommandId(commandId);
    if (!id) {
      return;
    }

    const state = this.stateFor(id);
    this.clearTimer(state);
    state.pending = null;
    await this.send(id, {
      progress: this.normalizeProgress(progress),
      message,
      ...(jobId ? { jobId } : {})
    });
  }

  clear(commandId: string | null | undefined): void {
    const id = this.cleanCommandId(commandId);
    if (!id) {
      return;
    }

    const state = this.states.get(id);
    if (state) {
      this.clearTimer(state);
    }
    this.states.delete(id);
  }

  private schedule(commandId: string): void {
    const state = this.stateFor(commandId);
    if (state.timer !== null) {
      return;
    }

    const elapsed = Date.now() - state.lastSentAt;
    const delay = state.inFlight
      ? this.minIntervalMs
      : Math.max(0, this.minIntervalMs - elapsed);

    state.timer = setTimeout(() => {
      state.timer = null;
      void this.flush(commandId);
    }, delay);
  }

  private async flush(commandId: string): Promise<void> {
    const state = this.stateFor(commandId);
    if (state.inFlight) {
      this.schedule(commandId);
      return;
    }

    const snapshot = state.pending;
    if (!snapshot) {
      return;
    }

    state.pending = null;
    await this.send(commandId, snapshot);
    if (state.pending) {
      this.schedule(commandId);
    }
  }

  private async send(commandId: string, snapshot: ProgressSnapshot): Promise<void> {
    const state = this.stateFor(commandId);
    state.inFlight = true;
    try {
      await firstValueFrom(this.commandService.updateProgress(
        commandId,
        snapshot.progress,
        snapshot.message,
        snapshot.jobId
      ).pipe(timeout(this.timeoutMs)));
      state.lastSentAt = Date.now();
    } catch {
      // Progress is best-effort; rendering must keep going even if a heartbeat fails.
    } finally {
      state.inFlight = false;
    }
  }

  private stateFor(commandId: string): ProgressState {
    let state = this.states.get(commandId);
    if (!state) {
      state = {
        lastSentAt: 0,
        inFlight: false,
        timer: null,
        pending: null
      };
      this.states.set(commandId, state);
    }
    return state;
  }

  private clearTimer(state: ProgressState): void {
    if (state.timer !== null) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }

  private normalizeProgress(progress: number): number {
    return Math.max(0, Math.min(100, Math.round(progress)));
  }

  private cleanCommandId(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }
}
